import {
  BPM_MAX,
  BPM_MIN,
  GENERATOR_DURATION_MAX,
  GENERATOR_DURATION_MIN,
  type FocusLevel,
  type Genre,
  type Instrument,
  type Mood,
  type MusicalKey,
  type SectionKind,
} from '../domain/schemas';
import { parsePitchClass, type ScaleName } from '../music/theory';
import type { Command, GenerateSpec, ParseError } from './commands';

/**
 * Deterministic grammar for music, mixing, arrangement and composition prompts.
 * Nothing here guesses: unknown words make the parse fail with a clear message.
 */

export type GrammarResult = { command: Command } | { error: ParseError } | null;

const err = (segment: string, message: string, suggestions: string[] = [], kind: ParseError['kind'] = 'invalid-value'): GrammarResult => ({
  error: { kind, message, segment, suggestions },
});

/** Instrument names and synonyms, longest first so multi-word names win. */
export const INSTRUMENT_WORDS: [string, Instrument][] = (
  [
    ['kick drums', 'kick'], ['kick drum', 'kick'], ['kicks', 'kick'], ['kick', 'kick'],
    ['snares', 'snare'], ['snare', 'snare'],
    ['claps', 'clap'], ['clap', 'clap'],
    ['hi-hats', 'hats'], ['hi-hat', 'hats'], ['hi hats', 'hats'], ['hi hat', 'hats'], ['hihats', 'hats'], ['hihat', 'hats'], ['hats', 'hats'], ['hat', 'hats'],
    ['percussion', 'perc'], ['perc', 'perc'], ['congas', 'perc'], ['conga', 'perc'], ['toms', 'perc'], ['tom', 'perc'],
    ['808 bass', 'bass808'], ['808s', 'bass808'], ['808', 'bass808'], ['bassline', 'bass808'], ['sub bass', 'bass808'], ['bass', 'bass808'],
    ['cowbells', 'cowbell'], ['cowbell', 'cowbell'], ['bells', 'cowbell'],
    ['synth lead', 'lead'], ['lead synth', 'lead'], ['leads', 'lead'], ['lead', 'lead'], ['synth', 'lead'],
    ['pads', 'pad'], ['pad', 'pad'], ['chords', 'pad'],
  ] as [string, Instrument][]
).sort((a, b) => b[0].length - a[0].length);

const INSTRUMENT_RE = INSTRUMENT_WORDS.map(([w]) => w.replace(/[-]/g, '\\-')).join('|');

export function parseInstrument(text: string): Instrument | undefined {
  const t = text.trim().replace(/^(the|a|an|my)\s+/, '');
  return INSTRUMENT_WORDS.find(([w]) => w === t)?.[1];
}

export const SECTION_WORDS: Record<string, SectionKind> = {
  intro: 'intro',
  introduction: 'intro',
  verse: 'verse',
  buildup: 'buildup',
  'build-up': 'buildup',
  'build up': 'buildup',
  build: 'buildup',
  riser: 'buildup',
  drop: 'drop',
  chorus: 'drop',
  hook: 'drop',
  breakdown: 'breakdown',
  'break down': 'breakdown',
  bridge: 'breakdown',
  outro: 'outro',
  ending: 'outro',
};
const SECTION_RE = Object.keys(SECTION_WORDS).sort((a, b) => b.length - a.length).join('|');

const GENRE_MATCHERS: [RegExp, Genre][] = [
  [/\b(brazilian|brazil|funk|mandelao|mandelão)( phonk| funk)?\b/, 'brazilian-phonk'],
  [/\bdrift( phonk)?\b/, 'drift-phonk'],
  [/\b(memphis( phonk)?|dark phonk)\b/, 'dark-phonk'],
  [/\btrap\b/, 'trap'],
  [/\b(lo-?fi( hip[- ]?hop)?|lo fi( hip[- ]?hop)?|chillhop)\b/, 'lofi'],
  [/\bphonk\b/, 'drift-phonk'],
];

export const GENRE_NAMES = 'brazilian phonk, drift phonk, dark phonk, trap or lo-fi';

const SCALE_WORDS: Record<string, ScaleName> = {
  'harmonic minor': 'harmonicMinor',
  minor: 'minor',
  min: 'minor',
  m: 'minor',
  major: 'major',
  maj: 'major',
  phrygian: 'phrygian',
  dorian: 'dorian',
};

const MOOD_WORDS: Record<string, Mood> = {
  dark: 'dark', sinister: 'dark', evil: 'dark', creepy: 'dark', eerie: 'dark',
  aggressive: 'aggressive', angry: 'aggressive', hard: 'aggressive', brutal: 'aggressive',
  chill: 'chill', chilled: 'chill', relaxed: 'chill', mellow: 'chill', calm: 'chill', smooth: 'chill',
  sad: 'melancholic', melancholic: 'melancholic', melancholy: 'melancholic', emotional: 'melancholic',
  energetic: 'energetic', hype: 'energetic', upbeat: 'energetic', bouncy: 'energetic',
};
const HEAVY_WORDS = new Set(['heavy', 'loud', 'big', 'strong', 'aggressive', 'hard', 'distorted', 'booming', 'punchy', 'fat', 'prominent', 'huge']);
const SUBTLE_WORDS = new Set(['subtle', 'soft', 'quiet', 'light', 'gentle', 'faint']);
const INTENSITY_WORDS: Record<string, number> = { intense: 0.9, heavy: 0.85, 'hard-hitting': 0.9, 'high-energy': 0.9, banging: 0.9, gentle: 0.35, soft: 0.35, light: 0.35, laid: 0.35 };
const COMPLEXITY_WORDS: Record<string, number> = { simple: 0.2, minimal: 0.2, sparse: 0.25, complex: 0.85, busy: 0.85, intricate: 0.9, detailed: 0.8 };
const FILLER = new Set([
  'a', 'an', 'the', 'some', 'me', 'us', 'my', 'new', 'full', 'whole', 'quick', 'short', 'long',
  'beat', 'beats', 'track', 'tracks', 'song', 'songs', 'tune', 'instrumental', 'arrangement', 'loop', 'music', 'piece', 'composition', 'type', 'style', 'vibe', 'vibes', 'feel', 'sound',
  'with', 'and', 'plus', 'at', 'in', 'of', 'for', 'that', 'has', 'using', 'featuring', 'lots', 'lot', 'very', 'really', 'super', 'kinda', 'pretty', 'back', 'hip-hop', 'hip', 'hop', 'phonk', 'funk', 'tempo', 'bpm', 'long', 'about', 'around', 'roughly', 'of',
]);

const GENERATE_VERB = /^(generate|create|make|compose|produce|build|give me|write|cook up|craft)\b/;
const MUSIC_NOUN = /\b(beat|track|song|tune|instrumental|arrangement|loop|music|melody|composition|phonk|trap|lo-?fi|lo fi|chillhop|funk)\b/;

/** True when a segment is a composition request (so the splitter must not cut it at "and"). */
export function isGenerateSegment(text: string): boolean {
  return GENERATE_VERB.test(text) && MUSIC_NOUN.test(text) && !/^make (the|it)\b/.test(text);
}

const NUMBER_WORDS: Record<string, number> = { a: 1, one: 1, two: 2, three: 3, four: 4, five: 5, half: 0.5 };

export function parseGenerate(segment: string): GrammarResult {
  if (!isGenerateSegment(segment)) return null;
  let rest = ` ${segment.replace(GENERATE_VERB, ' ')} `;
  const take = (re: RegExp): RegExpMatchArray | null => {
    const m = rest.match(re);
    if (m) rest = rest.replace(m[0], ' ');
    return m;
  };
  if (/\b(video|movie|film|footage|visuali[sz]er)\b/.test(rest)) {
    return {
      error: {
        kind: 'generation-unavailable',
        generationKind: 'text-to-video',
        segment,
        message: 'That needs text-to-video generation, which has no configured provider. Nothing was generated.',
        suggestions: ['start animation'],
      },
    };
  }
  if (/\b(vocals?|voice|singing|rap(ping)?|lyrics|sung)\b/.test(rest)) {
    return {
      error: {
        kind: 'generation-unavailable',
        generationKind: 'speech',
        segment,
        message: 'Vocals need speech synthesis, which has no configured provider. Nothing was generated. Remove the vocal request to compose the instrumental.',
        suggestions: [],
      },
    };
  }
  let genre: Genre | undefined;
  for (const [re, g] of GENRE_MATCHERS) {
    const m = rest.match(re);
    if (m) {
      genre = g;
      rest = rest.replace(m[0], ' ');
      break;
    }
  }
  if (!genre) {
    return err(segment, `Procedural composition needs a style: ${GENRE_NAMES}.`, ['create a 60-second drift phonk arrangement']);
  }
  const spec: GenerateSpec = { genre, focus: {} };
  const bpm = take(/\b(?:at )?(\d{2,3}(?:\.\d+)?) ?bpm\b/) ?? take(/\bat (\d{2,3})\b/);
  if (bpm) {
    const v = Number(bpm[1]);
    if (!Number.isInteger(v) || v < BPM_MIN || v > BPM_MAX) return err(segment, `BPM must be a whole number between ${BPM_MIN} and ${BPM_MAX}.`, ['generate a trap beat at 140 bpm']);
    spec.bpm = v;
  }
  const seed = take(/\b(?:with )?seed (\S+)/);
  if (seed) {
    const v = Number(seed[1]);
    if (!Number.isInteger(v) || v < 0 || v > 2 ** 31 - 1) return err(segment, 'Seed must be a whole number from 0 to 2147483647.', ['create a trap beat seed 42']);
    spec.seed = v;
  }
  const duration =
    take(/\b(\d+(?:\.\d+)?|a|one|two|three|four|five|half a)[- ]?(seconds?|secs?|s|minutes?|mins?)\b(?: long)?/) ?? null;
  if (duration) {
    const raw = duration[1]!;
    const n = NUMBER_WORDS[raw.replace(' a', '')] ?? Number(raw);
    const seconds = Math.round(/^m/.test(duration[2]!) ? n * 60 : n);
    if (!Number.isFinite(seconds) || seconds < GENERATOR_DURATION_MIN || seconds > GENERATOR_DURATION_MAX) {
      return err(segment, `Duration must be between ${GENERATOR_DURATION_MIN} seconds and ${GENERATOR_DURATION_MAX / 60} minutes.`, ['create a 60-second drift phonk arrangement']);
    }
    spec.durationSec = seconds;
  }
  // "in f minor", "key of c#", "in eb major". A bare "in a" needs a scale word, otherwise
  // "in a dark mood" would be read as A minor.
  const key =
    take(/\b(?:in(?: the key of)?|key(?: of)?) ([a-g])(#|b| sharp| flat)? ?(harmonic minor|minor|major|phrygian|dorian|min|maj|m)\b/) ??
    take(/\b(?:in the key of|key(?: of)?) ([a-g])(#|b| sharp| flat)?()(?=\s|$)/) ??
    take(/\bin ([b-g])(#|b| sharp| flat)?()(?=\s(?:with|at|and|seed)\b|\s*$)/);
  if (key) {
    const root = parsePitchClass(`${key[1]}${(key[2] ?? '').trim()}`);
    const scale = SCALE_WORDS[key[3] ?? 'minor'];
    if (root === undefined || !scale) return err(segment, 'Could not read the key.', ['in f minor']);
    spec.key = { root, scale } as MusicalKey;
  }
  // Remaining words: moods, focus phrases, intensity/complexity words, fillers.
  const unknown: string[] = [];
  const instrumentAt = (text: string): [Instrument, number] | null => {
    const m = text.match(new RegExp(`^(${INSTRUMENT_RE})\\b`));
    return m ? [INSTRUMENT_WORDS.find(([w]) => w === m[1])![1], m[1]!.split(' ').length] : null;
  };
  const words = rest.trim().split(/\s+/).filter(Boolean);
  for (let i = 0; i < words.length; i++) {
    const word = words[i]!.replace(/[,]$/, '');
    const following = words.slice(i + 1).join(' ');
    const inst = instrumentAt(following);
    if ((HEAVY_WORDS.has(word) || SUBTLE_WORDS.has(word)) && inst) {
      spec.focus[inst[0]] = (HEAVY_WORDS.has(word) ? 'heavy' : 'subtle') as FocusLevel;
      i += inst[1];
      continue;
    }
    const self = instrumentAt(words.slice(i).join(' '));
    if (self) {
      spec.focus[self[0]] ??= 'normal';
      i += self[1] - 1;
      continue;
    }
    if (MOOD_WORDS[word]) {
      spec.mood ??= MOOD_WORDS[word];
      if (word === 'aggressive' || word === 'hard') spec.intensity ??= 0.9;
      continue;
    }
    if (INTENSITY_WORDS[word] !== undefined) {
      spec.intensity = INTENSITY_WORDS[word];
      continue;
    }
    if (COMPLEXITY_WORDS[word] !== undefined) {
      spec.complexity = COMPLEXITY_WORDS[word];
      continue;
    }
    if (FILLER.has(word) || word === 'back') continue;
    unknown.push(word);
  }
  // Normal focus carries no information; drop it to keep parameters minimal.
  for (const [k, v] of Object.entries(spec.focus)) if (v === 'normal') delete spec.focus[k as Instrument];
  if (unknown.length) {
    return err(
      segment,
      `I can't apply "${unknown.join(' ')}" to a composition. Supported: genre, BPM, key, mood, intensity, complexity, duration, seed and heavy/subtle instruments.`,
      ['generate a dark brazilian phonk beat at 150 bpm in f minor with heavy 808 bass and aggressive cowbells'],
      'unsupported',
    );
  }
  return { command: { type: 'music.generate', spec } };
}

/** Mixer, intensity, arrangement, key, mode and export commands. */
export function parseMusicSegment(segment: string): GrammarResult {
  let m: RegExpMatchArray | null;
  const inst = `((?:the |my )?(?:${INSTRUMENT_RE}))`;

  // ---- Play song / pattern
  if ((m = segment.match(/^(?:play|start) (?:the )?(song|arrangement|pattern|loop)$/))) {
    return { command: { type: 'music.play', mode: m[1] === 'pattern' || m[1] === 'loop' ? 'pattern' : 'song' } };
  }
  if ((m = segment.match(/^(song|pattern|arrangement) mode$/))) {
    return { command: { type: 'music.setMode', mode: m[1] === 'pattern' ? 'pattern' : 'song' } };
  }
  if (/^(loop|repeat) (the )?(song|arrangement)$/.test(segment)) return { command: { type: 'music.loopSong', loop: true } };
  if (/^(don'?t|do not|stop) (loop|looping|repeat|repeating)( the)?( song| arrangement)?$/.test(segment)) {
    return { command: { type: 'music.loopSong', loop: false } };
  }

  // ---- Intensity
  if (
    (m = segment.match(/^make (?:the |it )?(drums|beat|percussion|bass|808s?|melody|melodies|everything|it|track|song|mix)? ?(?:sound )?(more|less) (intense|aggressive|energetic|busy|hard|powerful)$/)) ||
    (m = segment.match(/^(?:make )?(more|less) (intense|aggressive|energetic) (drums|beat|percussion|bass|808s?|melody|melodies)$/))
  ) {
    const groupWord = m[1] === 'more' || m[1] === 'less' ? m[3]! : m[1] ?? 'everything';
    const dirWord = m[1] === 'more' || m[1] === 'less' ? m[1] : m[2]!;
    const group = /drum|beat|perc/.test(groupWord) ? 'drums' : /bass|808/.test(groupWord) ? 'bass' : /melod/.test(groupWord) ? 'melody' : 'all';
    return { command: { type: 'music.adjustIntensity', group, direction: dirWord === 'more' ? 1 : -1 } };
  }
  if ((m = segment.match(/^(intensify|calm down|tone down|soften) (?:the )?(drums|beat|bass|808s?|melody|everything)$/))) {
    const group = /drum|beat/.test(m[2]!) ? 'drums' : /bass|808/.test(m[2]!) ? 'bass' : m[2] === 'melody' ? 'melody' : 'all';
    return { command: { type: 'music.adjustIntensity', group, direction: m[1] === 'intensify' ? 1 : -1 } };
  }

  // ---- Volume
  const dbOf = (raw: string | undefined, fallback: number) => (raw ? Number(raw) : fallback);
  if (
    (m = segment.match(new RegExp(`^(reduce|lower|decrease|turn down|cut|drop|raise|increase|boost|turn up|bring up|bring down) ${inst}(?: volume| level)?(?: by (\\d+(?:\\.\\d+)?) ?db)?$`))) ||
    (m = segment.match(new RegExp(`^(reduce|lower|decrease|turn down|raise|increase|boost|turn up) (?:the )?(?:volume|level) of ${inst}(?: by (\\d+(?:\\.\\d+)?) ?db)?$`)))
  ) {
    const target = parseInstrument(m[2]!)!;
    const down = /reduce|lower|decrease|down|cut|drop/.test(m[1]!);
    const amount = dbOf(m[3], 3);
    if (amount <= 0 || amount > 24) return err(segment, 'Volume changes must be between 0.5 and 24 dB.', ['reduce the cowbell volume by 3 db']);
    return { command: { type: 'music.adjustVolume', target, deltaDb: down ? -amount : amount } };
  }
  if ((m = segment.match(new RegExp(`^make ${inst} (louder|quieter|softer)$`)))) {
    return { command: { type: 'music.adjustVolume', target: parseInstrument(m[1]!)!, deltaDb: m[2] === 'louder' ? 3 : -3 } };
  }
  if ((m = segment.match(new RegExp(`^(?:set )?${inst} (?:volume|level)(?: to| =)? (-?\\d+(?:\\.\\d+)?) ?db$`)))) {
    const db = Number(m[2]);
    if (db < -60 || db > 6) return err(segment, 'Track volume must be between -60 and +6 dB.', ['set kick volume to -3 db']);
    return { command: { type: 'music.setVolume', target: parseInstrument(m[1]!)!, db } };
  }
  if ((m = segment.match(/^(?:set )?(?:the )?master(?: volume| gain)?(?: to| =)? (-?\d+(?:\.\d+)?) ?db$/))) {
    const db = Number(m[1]);
    if (db < -60 || db > 0) return err(segment, 'Master volume must be between -60 and 0 dB.', ['set master volume to -6 db']);
    return { command: { type: 'music.setVolume', target: 'master', db } };
  }
  if ((m = segment.match(/^(turn (?:up|down)|raise|lower|reduce|boost) (?:the )?master(?: volume| gain)?$/))) {
    return { command: { type: 'music.adjustVolume', target: 'master', deltaDb: /up|raise|boost/.test(m[1]!) ? 3 : -3 } };
  }

  // ---- Solo / pan / effects
  if ((m = segment.match(new RegExp(`^(solo|unsolo) ${inst}$`)))) {
    return { command: { type: 'music.solo', instrument: parseInstrument(m[2]!)!, solo: m[1] === 'solo' } };
  }
  if ((m = segment.match(new RegExp(`^pan ${inst} (?:to the )?(hard left|left|center|centre|middle|right|hard right)$`)))) {
    const pan = { 'hard left': -1, left: -0.5, center: 0, centre: 0, middle: 0, right: 0.5, 'hard right': 1 }[m[2]!]!;
    return { command: { type: 'music.pan', instrument: parseInstrument(m[1]!)!, pan } };
  }
  if ((m = segment.match(new RegExp(`^(more|less|add more|add less|add) (distortion|drive|saturation|reverb|delay|echo) (?:on|to) ${inst}$`)))) {
    const effect = /reverb/.test(m[2]!) ? 'reverbSend' : /delay|echo/.test(m[2]!) ? 'delaySend' : 'drive';
    return { command: { type: 'music.adjustEffect', instrument: parseInstrument(m[3]!)!, effect, delta: /less/.test(m[1]!) ? -0.2 : 0.2 } };
  }

  // ---- Key
  if ((m = segment.match(/^(?:set |change )?(?:the )?key(?: to| =)? ([a-g])(#|b| sharp| flat)? ?(harmonic minor|minor|major|phrygian|dorian|min|maj|m)?$/))) {
    const root = parsePitchClass(`${m[1]}${(m[2] ?? '').trim()}`);
    const scale = SCALE_WORDS[m[3] ?? 'minor'];
    if (root === undefined || !scale) return err(segment, 'Could not read the key.', ['set key to f# minor']);
    return { command: { type: 'music.setKey', key: { root, scale } } };
  }

  // ---- Arrangement
  const kindOf = (w: string) => SECTION_WORDS[w];
  if ((m = segment.match(new RegExp(`^(?:add|insert|put) (?:a |an |another )?(${SECTION_RE})(?: section)? (before|after) (?:the )?(${SECTION_RE})$`)))) {
    return { command: { type: 'arrangement.insert', kind: kindOf(m[1]!)!, relation: m[2] as 'before' | 'after', anchor: kindOf(m[3]!)! } };
  }
  if ((m = segment.match(new RegExp(`^(?:add|insert|append) (?:a |an |another )?(${SECTION_RE})(?: section)?(?: (?:at|to) the (end|start|beginning))?$`)))) {
    return { command: { type: 'arrangement.append', kind: kindOf(m[1]!)!, at: m[2] === 'start' || m[2] === 'beginning' ? 'start' : 'end' } };
  }
  if ((m = segment.match(new RegExp(`^(?:remove|delete|cut|drop) (?:the )?(${SECTION_RE})(?: section)?$`)))) {
    return { command: { type: 'arrangement.remove', kind: kindOf(m[1]!)! } };
  }
  if ((m = segment.match(new RegExp(`^(?:duplicate|repeat|double) (?:the )?(${SECTION_RE})(?: section)?$`)))) {
    return { command: { type: 'arrangement.duplicate', kind: kindOf(m[1]!)! } };
  }
  if ((m = segment.match(new RegExp(`^make (?:the )?(${SECTION_RE})(?: section)? (longer|shorter)(?: by (\\d+) bars?)?$`)))) {
    const bars = m[3] ? Number(m[3]) : 4;
    if (bars < 1 || bars > 32) return err(segment, 'Change sections by 1–32 bars.', ['make the drop longer by 4 bars']);
    return { command: { type: 'arrangement.resize', kind: kindOf(m[1]!)!, deltaBars: m[2] === 'longer' ? bars : -bars } };
  }

  // ---- Export
  if (/^(?:export|render|download|bounce|save)(?: the| a| my)?(?: song| track| beat| arrangement| audio| mix)?(?: (?:as|to))?(?: a)? wav(?: file)?$/.test(segment)) {
    return { command: { type: 'export.wav' } };
  }
  return null;
}
